import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, FileTextIcon, SaveIcon, ArrowLeftIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, CalendarIcon, UserIcon, PhoneIcon, MailIcon, GlobeIcon, MapPinIcon, BarChartIcon, ExternalLinkIcon, CreditCardIcon, BuildingIcon, ThumbsUpIcon, ThumbsDownIcon, CheckIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function VendorDetails({
  vendor,
  onUpdate,
  setCurrentView,
  entities = [],
  locale = 'en-US'
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ...vendor,
    newContactName: '',
    newContactEmail: '',
    newContactPhone: '',
    newContactRole: '',
    // For address editing
    newAddressStreet: '',
    newAddressCity: '',
    newAddressState: '',
    newAddressPostalCode: '',
    newAddressCountry: '',
    newAddressType: 'Physical',
    newAddressIsPrimary: false,
    // For bank account editing
    newBankName: '',
    newBankAccountNumber: '',
    newBankRoutingNumber: '',
    newBankSwiftCode: '',
    newBankAccountName: '',
    newBankCurrency: '',
    newBankBranchName: '',
    newBankAccountType: 'Checking',
    newBankIsPrimary: false
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showBankForm, setShowBankForm] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [isESGEnabled] = useState(true); // In a real app, this would come from tenant settings
  const [hasESGViewPermission] = useState(true); // In a real app, this would come from user permissions
  const [hasESGEditPermission] = useState(true); // In a real app, this would come from user permissions
  // Add new state for invoice extracted bank details
  const [extractedBankDetails, setExtractedBankDetails] = useState({
    accountName: 'PrintWorks Inc.',
    accountNumber: '4567890123',
    routingNumber: '021000021',
    bankName: 'Chase Bank',
    swiftCode: 'CHASUS33',
    extractedFrom: 'INV-2023-4501',
    extractionDate: '2023-10-15'
  });
  // Add state for EFTSure verification status
  const [eftSureVerification, setEftSureVerification] = useState({
    status: 'mismatch',
    verifiedDate: '2023-10-15',
    mismatchFields: ['accountNumber']
  });
  const vendorTypes = ['Supplier', 'Manufacturer', 'Distributor', 'Service Provider', 'Consultant', 'Contractor', 'Other'];
  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'China', 'India'];
  const addressTypes = ['Physical', 'Billing', 'Shipping', 'Mailing', 'Registered', 'Other'];
  const bankAccountTypes = ['Checking', 'Savings', 'Business', 'Current', 'Investment', 'Other'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR'];
  const getEntityNames = () => {
    if (vendor.isGlobal) return 'All Entities';
    if (vendor.entities && vendor.entities.length > 0) {
      return vendor.entities.map(entityId => {
        const entity = entities.find(e => e.id === entityId);
        return entity ? entity.name : entityId;
      }).join(', ');
    }
    return 'None';
  };
  const getSiteNames = () => {
    if (vendor.isGlobal) return 'All Sites';
    if (vendor.sites && vendor.sites.length > 0) {
      const siteNames = [];
      vendor.entities.forEach(entityId => {
        const entity = entities.find(e => e.id === entityId);
        if (entity && entity.sites) {
          vendor.sites.forEach(siteId => {
            const site = entity.sites.find(s => s.id === siteId);
            if (site) {
              siteNames.push(`${site.name} (${entity.name})`);
            }
          });
        }
      });
      return siteNames.join(', ');
    }
    if (vendor.entities && vendor.entities.length > 0) {
      return 'All sites within selected entities';
    }
    return 'None';
  };
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const addContact = () => {
    if (!formData.newContactName || !formData.newContactEmail) {
      setErrors({
        ...errors,
        newContactName: !formData.newContactName ? 'Contact name is required' : null,
        newContactEmail: !formData.newContactEmail ? 'Email is required' : null
      });
      return;
    }
    const newContact = {
      id: formData.contacts.length > 0 ? Math.max(...formData.contacts.map(c => c.id)) + 1 : 1,
      name: formData.newContactName,
      email: formData.newContactEmail,
      phone: formData.newContactPhone || '',
      role: formData.newContactRole || ''
    };
    setFormData({
      ...formData,
      contacts: [...formData.contacts, newContact],
      newContactName: '',
      newContactEmail: '',
      newContactPhone: '',
      newContactRole: ''
    });
  };
  const removeContact = id => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter(contact => contact.id !== id)
    });
  };
  const handleAddAddress = () => {
    setShowAddressForm(true);
    setEditingAddress(null);
    // Reset address form fields
    setFormData({
      ...formData,
      newAddressStreet: '',
      newAddressCity: '',
      newAddressState: '',
      newAddressPostalCode: '',
      newAddressCountry: '',
      newAddressType: 'Physical',
      newAddressIsPrimary: false
    });
  };
  const handleEditAddress = address => {
    setShowAddressForm(true);
    setEditingAddress(address);
    setFormData({
      ...formData,
      newAddressStreet: address.street,
      newAddressCity: address.city,
      newAddressState: address.state || '',
      newAddressPostalCode: address.postalCode || '',
      newAddressCountry: address.country,
      newAddressType: address.addressType || 'Physical',
      newAddressIsPrimary: address.isPrimary || false
    });
  };
  const saveAddress = () => {
    // Validate required fields
    if (!formData.newAddressStreet || !formData.newAddressCity || !formData.newAddressCountry) {
      setErrors({
        ...errors,
        newAddressStreet: !formData.newAddressStreet ? 'Street is required' : null,
        newAddressCity: !formData.newAddressCity ? 'City is required' : null,
        newAddressCountry: !formData.newAddressCountry ? 'Country is required' : null
      });
      return;
    }
    const newAddress = {
      id: editingAddress ? editingAddress.id : formData.addresses && formData.addresses.length > 0 ? Math.max(...formData.addresses.map(a => a.id)) + 1 : 1,
      street: formData.newAddressStreet,
      city: formData.newAddressCity,
      state: formData.newAddressState,
      postalCode: formData.newAddressPostalCode,
      country: formData.newAddressCountry,
      addressType: formData.newAddressType,
      isPrimary: formData.newAddressIsPrimary
    };
    let updatedAddresses = [];
    if (editingAddress) {
      // Update existing address
      updatedAddresses = formData.addresses.map(addr => addr.id === editingAddress.id ? newAddress : addr);
    } else {
      // Add new address
      const addresses = formData.addresses || [];
      // If this is marked as primary, update all others to non-primary
      if (newAddress.isPrimary) {
        updatedAddresses = [...addresses.map(addr => ({
          ...addr,
          isPrimary: false
        })), newAddress];
      } else if (addresses.length === 0) {
        // First address should be primary by default
        updatedAddresses = [{
          ...newAddress,
          isPrimary: true
        }];
      } else {
        updatedAddresses = [...addresses, newAddress];
      }
    }
    setFormData({
      ...formData,
      addresses: updatedAddresses,
      newAddressStreet: '',
      newAddressCity: '',
      newAddressState: '',
      newAddressPostalCode: '',
      newAddressCountry: '',
      newAddressType: 'Physical',
      newAddressIsPrimary: false
    });
    setShowAddressForm(false);
    setEditingAddress(null);
  };
  const removeAddress = id => {
    const addressToRemove = formData.addresses.find(addr => addr.id === id);
    const wasRemovingPrimary = addressToRemove && addressToRemove.isPrimary;
    const filteredAddresses = formData.addresses.filter(addr => addr.id !== id);
    // If we removed the primary address and there are other addresses left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAddresses.length > 0) {
      filteredAddresses[0].isPrimary = true;
    }
    setFormData({
      ...formData,
      addresses: filteredAddresses
    });
  };
  // Bank account management functions
  const handleAddBankAccount = () => {
    setShowBankForm(true);
    setEditingBank(null);
    // Reset bank form fields
    setFormData({
      ...formData,
      newBankName: '',
      newBankAccountNumber: '',
      newBankRoutingNumber: '',
      newBankSwiftCode: '',
      newBankAccountName: '',
      newBankCurrency: 'USD',
      newBankBranchName: '',
      newBankAccountType: 'Checking',
      newBankIsPrimary: false
    });
  };
  const handleEditBankAccount = bank => {
    setShowBankForm(true);
    setEditingBank(bank);
    setFormData({
      ...formData,
      newBankName: bank.bankName,
      newBankAccountNumber: bank.accountNumber,
      newBankRoutingNumber: bank.routingNumber || '',
      newBankSwiftCode: bank.swiftCode || '',
      newBankAccountName: bank.accountName,
      newBankCurrency: bank.currency || 'USD',
      newBankBranchName: bank.branchName || '',
      newBankAccountType: bank.accountType || 'Checking',
      newBankIsPrimary: bank.isPrimary || false
    });
  };
  const saveBankAccount = () => {
    // Validate required fields
    if (!formData.newBankName || !formData.newBankAccountNumber || !formData.newBankAccountName) {
      setErrors({
        ...errors,
        newBankName: !formData.newBankName ? 'Bank name is required' : null,
        newBankAccountNumber: !formData.newBankAccountNumber ? 'Account number is required' : null,
        newBankAccountName: !formData.newBankAccountName ? 'Account name is required' : null
      });
      return;
    }
    // Simulate compliance check - in a real app, this would call an external API
    const complianceStatus = Math.random() > 0.3 ? 'Verified' : 'Failed';
    const complianceDate = new Date().toISOString().split('T')[0];
    const complianceProvider = 'Eftsure';
    const newBank = {
      id: editingBank ? editingBank.id : formData.bankAccounts && formData.bankAccounts.length > 0 ? Math.max(...formData.bankAccounts.map(b => b.id)) + 1 : 1,
      bankName: formData.newBankName,
      accountNumber: formData.newBankAccountNumber,
      routingNumber: formData.newBankRoutingNumber,
      swiftCode: formData.newBankSwiftCode,
      accountName: formData.newBankAccountName,
      currency: formData.newBankCurrency,
      branchName: formData.newBankBranchName,
      accountType: formData.newBankAccountType,
      isPrimary: formData.newBankIsPrimary,
      complianceStatus,
      complianceDate,
      complianceProvider
    };
    let updatedBankAccounts = [];
    if (editingBank) {
      // Update existing bank account
      updatedBankAccounts = formData.bankAccounts.map(bank => bank.id === editingBank.id ? newBank : bank);
    } else {
      // Add new bank account
      const bankAccounts = formData.bankAccounts || [];
      // If this is marked as primary, update all others to non-primary
      if (newBank.isPrimary) {
        updatedBankAccounts = [...bankAccounts.map(bank => ({
          ...bank,
          isPrimary: false
        })), newBank];
      } else if (bankAccounts.length === 0) {
        // First bank account should be primary by default
        updatedBankAccounts = [{
          ...newBank,
          isPrimary: true
        }];
      } else {
        updatedBankAccounts = [...bankAccounts, newBank];
      }
    }
    setFormData({
      ...formData,
      bankAccounts: updatedBankAccounts,
      newBankName: '',
      newBankAccountNumber: '',
      newBankRoutingNumber: '',
      newBankSwiftCode: '',
      newBankAccountName: '',
      newBankCurrency: 'USD',
      newBankBranchName: '',
      newBankAccountType: 'Checking',
      newBankIsPrimary: false
    });
    setShowBankForm(false);
    setEditingBank(null);
  };
  const removeBankAccount = id => {
    const bankToRemove = formData.bankAccounts.find(bank => bank.id === id);
    const wasRemovingPrimary = bankToRemove && bankToRemove.isPrimary;
    const filteredBankAccounts = formData.bankAccounts.filter(bank => bank.id !== id);
    // If we removed the primary bank and there are other banks left,
    // make the first one primary
    if (wasRemovingPrimary && filteredBankAccounts.length > 0) {
      filteredBankAccounts[0].isPrimary = true;
    }
    setFormData({
      ...formData,
      bankAccounts: filteredBankAccounts
    });
  };
  const getComplianceStatusIcon = status => {
    if (status === 'Verified') {
      return <ThumbsUpIcon className="h-5 w-5 text-green-500" data-id="element-19102" />;
    } else {
      return <ThumbsDownIcon className="h-5 w-5 text-red-500" data-id="element-19103" />;
    }
  };
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const handleSave = () => {
    // Validate form
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Vendor name is required';
    if (!formData.type) newErrors.type = 'Vendor type is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.contacts || formData.contacts.length === 0) newErrors.contacts = 'At least one contact is required';
    if (!formData.addresses || formData.addresses.length === 0) newErrors.addresses = 'At least one address is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Process new documents
    const newDocuments = files.map((file, index) => ({
      id: vendor.documents.length + index + 1,
      name: file.name,
      type: 'Supporting Document',
      uploadDate: new Date().toISOString().split('T')[0],
      expiryDate: null,
      status: 'Valid'
    }));
    // Update vendor
    const updatedVendor = {
      ...formData,
      documents: [...vendor.documents, ...newDocuments],
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    // Remove temporary form fields
    delete updatedVendor.newContactName;
    delete updatedVendor.newContactEmail;
    delete updatedVendor.newContactPhone;
    delete updatedVendor.newContactRole;
    delete updatedVendor.newAddressStreet;
    delete updatedVendor.newAddressCity;
    delete updatedVendor.newAddressState;
    delete updatedVendor.newAddressPostalCode;
    delete updatedVendor.newAddressCountry;
    delete updatedVendor.newAddressType;
    delete updatedVendor.newAddressIsPrimary;
    delete updatedVendor.newBankName;
    delete updatedVendor.newBankAccountNumber;
    delete updatedVendor.newBankRoutingNumber;
    delete updatedVendor.newBankSwiftCode;
    delete updatedVendor.newBankAccountName;
    delete updatedVendor.newBankCurrency;
    delete updatedVendor.newBankBranchName;
    delete updatedVendor.newBankAccountType;
    delete updatedVendor.newBankIsPrimary;
    onUpdate(updatedVendor);
    setIsEditing(false);
  };
  const getStatusBadge = status => {
    switch (status) {
      case 'Active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19104">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19105" />
            {status}
          </span>;
      case 'Pending Onboarding':
      case 'Invited':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-19106">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-19107" />
            {status}
          </span>;
      case 'Inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19108">
            <UserIcon className="mr-1 h-3 w-3" data-id="element-19109" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19110">
            {status}
          </span>;
    }
  };
  const getComplianceBadge = status => {
    switch (status) {
      case 'Compliant':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19111">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19112" />
            {status}
          </span>;
      case 'Pending Review':
      case 'Incomplete':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-19113">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-19114" />
            {status}
          </span>;
      case 'Non-Compliant':
      case 'Expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19115">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-19116" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19117">
            {status}
          </span>;
    }
  };
  const getDocumentStatusBadge = status => {
    switch (status) {
      case 'Valid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19118">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19119" />
            {status}
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-19120">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-19121" />
            {status}
          </span>;
      case 'Expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19122">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-19123" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19124">
            {status}
          </span>;
    }
  };
  // Helper function to get ESG status label and color based on score
  const getESGStatusInfo = score => {
    if (!score && score !== 0) return {
      label: 'Not Provided',
      color: 'gray'
    };
    if (score >= 80) return {
      label: 'Excellent',
      color: 'green'
    };
    if (score >= 60) return {
      label: 'Good',
      color: 'blue'
    };
    if (score >= 40) return {
      label: 'Moderate',
      color: 'yellow'
    };
    return {
      label: 'Needs Review',
      color: 'red'
    };
  };
  // Add function to check if extracted details match vendor bank details
  const checkBankDetailsMatch = () => {
    if (!extractedBankDetails || !vendor.bankAccounts || vendor.bankAccounts.length === 0) {
      return 'unavailable';
    }
    // Find primary bank account or use the first one
    const primaryBank = vendor.bankAccounts.find(bank => bank.isPrimary) || vendor.bankAccounts[0];
    // Check if key fields match
    const accountNumberMatch = primaryBank.accountNumber === extractedBankDetails.accountNumber;
    const accountNameMatch = primaryBank.accountName === extractedBankDetails.accountName;
    const routingNumberMatch = primaryBank.routingNumber === extractedBankDetails.routingNumber;
    const mismatchFields = [];
    if (!accountNumberMatch) mismatchFields.push('accountNumber');
    if (!accountNameMatch) mismatchFields.push('accountName');
    if (!routingNumberMatch) mismatchFields.push('routingNumber');
    // Update EFTSure verification status
    setEftSureVerification({
      status: mismatchFields.length === 0 ? 'match' : 'mismatch',
      verifiedDate: new Date().toISOString().split('T')[0],
      mismatchFields
    });
    return mismatchFields.length === 0 ? 'match' : 'mismatch';
  };
  // Call verification check when component mounts or when extracted details change
  useEffect(() => {
    checkBankDetailsMatch();
  }, [extractedBankDetails]);
  return <div data-id="element-19125">
      <div className="mb-8 flex items-center" data-id="element-19126">
        <button onClick={() => setCurrentView('vendors')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-19127">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-19128" />
        </button>
        <div data-id="element-19129">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-19130">{vendor.name}</h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19131">
            {vendor.id} • {vendor.type}
          </p>
        </div>
        <div className="ml-auto flex space-x-3" data-id="element-19132">
          {isEditing ? <>
              <button onClick={() => setIsEditing(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19133">
                Cancel
              </button>
              <button onClick={handleSave} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19134">
                <SaveIcon className="h-4 w-4 mr-1" data-id="element-19135" />
                Save Changes
              </button>
            </> : <button onClick={() => setIsEditing(true)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19136">
              Edit Vendor
            </button>}
        </div>
      </div>

      {/* ESG Overview Section - Only show if ESG is enabled for tenant and user has permission */}
      {isESGEnabled && hasESGViewPermission && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19137">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19138">
            <div data-id="element-19139">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19140">
                ESG Overview
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-19141">
                Environmental, Social, and Governance assessment
              </p>
            </div>
            {hasESGEditPermission && <button onClick={() => {}} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19142">
                Edit ESG Details
              </button>}
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0" data-id="element-19143">
            {vendor.esg ? <dl className="sm:divide-y sm:divide-gray-200" data-id="element-19144">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19145">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19146">
                    ESG Score
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19147">
                    <div className="flex items-center" data-id="element-19148">
                      <span className="text-2xl font-bold" data-id="element-19149">
                        {vendor.esg.score}
                      </span>
                      <span className="ml-2 text-sm" data-id="element-19150">/100</span>
                      {(() => {
                  const {
                    label,
                    color
                  } = getESGStatusInfo(vendor.esg.score);
                  return <span className={`ml-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`} data-id="element-19151">
                            {label}
                          </span>;
                })()}
                    </div>
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19152">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19153">
                    Last Updated
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19154">
                    {vendor.esg.lastUpdated}
                  </dd>
                </div>
                {vendor.esg.summary && <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19155">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-19156">
                      ESG Summary
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19157">
                      {vendor.esg.summary}
                    </dd>
                  </div>}
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19158">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19159">
                    Category Scores
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19160">
                    <div className="grid grid-cols-3 gap-4" data-id="element-19161">
                      <div data-id="element-19162">
                        <span className="block text-xs text-gray-500" data-id="element-19163">
                          Environmental
                        </span>
                        <div className="flex items-center mt-1" data-id="element-19164">
                          <span className="text-lg font-semibold" data-id="element-19165">
                            {vendor.esg.environmental}
                          </span>
                          <span className="ml-1 text-xs" data-id="element-19166">/100</span>
                        </div>
                      </div>
                      <div data-id="element-19167">
                        <span className="block text-xs text-gray-500" data-id="element-19168">
                          Social
                        </span>
                        <div className="flex items-center mt-1" data-id="element-19169">
                          <span className="text-lg font-semibold" data-id="element-19170">
                            {vendor.esg.social}
                          </span>
                          <span className="ml-1 text-xs" data-id="element-19171">/100</span>
                        </div>
                      </div>
                      <div data-id="element-19172">
                        <span className="block text-xs text-gray-500" data-id="element-19173">
                          Governance
                        </span>
                        <div className="flex items-center mt-1" data-id="element-19174">
                          <span className="text-lg font-semibold" data-id="element-19175">
                            {vendor.esg.governance}
                          </span>
                          <span className="ml-1 text-xs" data-id="element-19176">/100</span>
                        </div>
                      </div>
                    </div>
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19177">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19178">Actions</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19179">
                    <a href="#" onClick={e => {
                e.preventDefault();
                /* In a real app, navigate to full ESG profile */
              }} className="inline-flex items-center text-indigo-600 hover:text-indigo-900" data-id="element-19180">
                      View Full ESG Profile
                      <ExternalLinkIcon className="ml-1 h-4 w-4" data-id="element-19181" />
                    </a>
                  </dd>
                </div>
              </dl> : <div className="px-4 py-5 text-center" data-id="element-19182">
                <BarChartIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-19183" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-19184">
                  No ESG data available
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-19185">
                  This vendor doesn't have ESG data recorded yet.
                </p>
                {hasESGEditPermission && <div className="mt-6" data-id="element-19186">
                    <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {}} data-id="element-19187">
                      <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" data-id="element-19188" />
                      Add ESG Data
                    </button>
                  </div>}
              </div>}
          </div>
        </div>}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19189">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19190">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19191">
            Vendor Information
          </h3>
          <div className="flex space-x-2" data-id="element-19192">
            {getStatusBadge(vendor.status)}
            {getComplianceBadge(vendor.complianceStatus)}
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0" data-id="element-19193">
          {isEditing ? <div className="px-4 py-5" data-id="element-19194">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-19195">
                <div className="sm:col-span-3" data-id="element-19196">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-19197">
                    Vendor Name *
                  </label>
                  <div className="mt-1" data-id="element-19198">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.name ? 'border-red-300' : ''}`} data-id="element-19199" />
                    {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-19200">{errors.name}</p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19201">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-19202">
                    Vendor Type *
                  </label>
                  <div className="mt-1" data-id="element-19203">
                    <select id="type" name="type" value={formData.type} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.type ? 'border-red-300' : ''}`} data-id="element-19204">
                      <option value="" data-id="element-19205">Select vendor type</option>
                      {vendorTypes.map(type => <option key={type} value={type} data-id="element-19206">
                          {type}
                        </option>)}
                    </select>
                    {errors.type && <p className="mt-1 text-sm text-red-600" data-id="element-19207">{errors.type}</p>}
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-19208">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-19209">
                    Description
                  </label>
                  <div className="mt-1" data-id="element-19210">
                    <textarea id="description" name="description" rows={3} value={formData.description || ''} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19211" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19212">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-19213">
                    Country *
                  </label>
                  <div className="mt-1" data-id="element-19214">
                    <select id="country" name="country" value={formData.country} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.country ? 'border-red-300' : ''}`} data-id="element-19215">
                      <option value="" data-id="element-19216">Select country</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-19217">
                          {country}
                        </option>)}
                    </select>
                    {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-19218">
                        {errors.country}
                      </p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19219">
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700" data-id="element-19220">
                    Tax ID / Business Number
                  </label>
                  <div className="mt-1" data-id="element-19221">
                    <input type="text" name="taxId" id="taxId" value={formData.taxId || ''} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19222" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19223">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700" data-id="element-19224">
                    Website
                  </label>
                  <div className="mt-1" data-id="element-19225">
                    <input type="url" name="website" id="website" value={formData.website || ''} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19226" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19227">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-19228">
                    Status
                  </label>
                  <div className="mt-1" data-id="element-19229">
                    <select id="status" name="status" value={formData.status} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19230">
                      <option value="Active" data-id="element-19231">Active</option>
                      <option value="Pending Onboarding" data-id="element-19232">
                        Pending Onboarding
                      </option>
                      <option value="Invited" data-id="element-19233">Invited</option>
                      <option value="Inactive" data-id="element-19234">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </div> : <dl className="sm:divide-y sm:divide-gray-200" data-id="element-19235">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19236">
                <dt className="text-sm font-medium text-gray-500" data-id="element-19237">
                  Vendor name
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19238">
                  {vendor.name}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19239">
                <dt className="text-sm font-medium text-gray-500" data-id="element-19240">Vendor ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19241">
                  {vendor.id}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19242">
                <dt className="text-sm font-medium text-gray-500" data-id="element-19243">Type</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19244">
                  {vendor.type}
                </dd>
              </div>
              {vendor.description && <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19245">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19246">
                    Description
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19247">
                    {vendor.description}
                  </dd>
                </div>}
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19248">
                <dt className="text-sm font-medium text-gray-500" data-id="element-19249">Country</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19250">
                  {vendor.country}
                </dd>
              </div>
              {vendor.taxId && <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19251">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19252">
                    Tax ID / Business Number
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19253">
                    {vendor.taxId}
                  </dd>
                </div>}
              {vendor.website && <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19254">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19255">Website</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19256">
                    <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900" data-id="element-19257">
                      {vendor.website}
                    </a>
                  </dd>
                </div>}
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19258">
                <dt className="text-sm font-medium text-gray-500" data-id="element-19259">
                  Onboarding Date
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19260">
                  {vendor.onboardingDate}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-19261">
                <dt className="text-sm font-medium text-gray-500" data-id="element-19262">
                  Last Updated
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-19263">
                  {vendor.lastUpdated}
                </dd>
              </div>
            </dl>}
        </div>
      </div>

      {/* Add new section for Invoice Extracted Bank Details after Vendor Information section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19264">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19265">
          <div data-id="element-19266">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19267">
              Invoice Bank Details Verification
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19268">
              Bank details extracted from invoice compared to vendor records
            </p>
          </div>
          <div className="flex space-x-2" data-id="element-19269">
            {eftSureVerification.status === 'match' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19270">
                <ThumbsUpIcon className="mr-1 h-4 w-4 text-green-500" data-id="element-19271" />
                EFTSure Verified
              </span> : eftSureVerification.status === 'mismatch' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19272">
                <ThumbsDownIcon className="mr-1 h-4 w-4 text-red-500" data-id="element-19273" />
                EFTSure Mismatch
              </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19274">
                <ClockIcon className="mr-1 h-4 w-4" data-id="element-19275" />
                Verification Pending
              </span>}
          </div>
        </div>
        <div className="border-t border-gray-200" data-id="element-19276">
          {extractedBankDetails ? <div className="px-4 py-5" data-id="element-19277">
              <div className="mb-4" data-id="element-19278">
                <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3" data-id="element-19279">
                  <FileTextIcon className="h-4 w-4" data-id="element-19280" />
                  <span data-id="element-19281">
                    Extracted from invoice:{' '}
                    <span className="font-medium" data-id="element-19282">
                      {extractedBankDetails.extractedFrom}
                    </span>{' '}
                    on {extractedBankDetails.extractionDate}
                  </span>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4" data-id="element-19283">
                  <div className="flex" data-id="element-19284">
                    <div className="flex-shrink-0" data-id="element-19285">
                      <AlertTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" data-id="element-19286" />
                    </div>
                    <div className="ml-3" data-id="element-19287">
                      <p className="text-sm text-yellow-700" data-id="element-19288">
                        Always verify bank details before processing payment.
                        Bank fraud is a common occurrence.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-19289">
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-19290">
                    <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-19291">
                      Extracted from Invoice
                    </h4>
                    <dl className="space-y-2" data-id="element-19292">
                      <div className="flex justify-between" data-id="element-19293">
                        <dt className="text-sm font-medium text-gray-500" data-id="element-19294">
                          Bank Name
                        </dt>
                        <dd className="text-sm text-gray-900" data-id="element-19295">
                          {extractedBankDetails.bankName}
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-19296">
                        <dt className="text-sm font-medium text-gray-500" data-id="element-19297">
                          Account Name
                        </dt>
                        <dd className={`text-sm ${eftSureVerification.mismatchFields.includes('accountName') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-19298">
                          {extractedBankDetails.accountName}
                          {eftSureVerification.mismatchFields.includes('accountName') && <AlertTriangleIcon className="inline-block h-4 w-4 ml-1 text-red-500" data-id="element-19299" />}
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-19300">
                        <dt className="text-sm font-medium text-gray-500" data-id="element-19301">
                          Account Number
                        </dt>
                        <dd className={`text-sm ${eftSureVerification.mismatchFields.includes('accountNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-19302">
                          {extractedBankDetails.accountNumber.substring(0, 4)}
                          {'•'.repeat(Math.max(0, extractedBankDetails.accountNumber.length - 8))}
                          {extractedBankDetails.accountNumber.substring(extractedBankDetails.accountNumber.length - 4)}
                          {eftSureVerification.mismatchFields.includes('accountNumber') && <AlertTriangleIcon className="inline-block h-4 w-4 ml-1 text-red-500" data-id="element-19303" />}
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-19304">
                        <dt className="text-sm font-medium text-gray-500" data-id="element-19305">
                          Routing Number
                        </dt>
                        <dd className={`text-sm ${eftSureVerification.mismatchFields.includes('routingNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-19306">
                          {extractedBankDetails.routingNumber}
                          {eftSureVerification.mismatchFields.includes('routingNumber') && <AlertTriangleIcon className="inline-block h-4 w-4 ml-1 text-red-500" data-id="element-19307" />}
                        </dd>
                      </div>
                      {extractedBankDetails.swiftCode && <div className="flex justify-between" data-id="element-19308">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-19309">
                            SWIFT/BIC Code
                          </dt>
                          <dd className="text-sm text-gray-900" data-id="element-19310">
                            {extractedBankDetails.swiftCode}
                          </dd>
                        </div>}
                    </dl>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-19311">
                    <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-19312">
                      Vendor Record (EFTSure)
                    </h4>
                    {vendor.bankAccounts && vendor.bankAccounts.length > 0 ? (() => {
                  const primaryBank = vendor.bankAccounts.find(bank => bank.isPrimary) || vendor.bankAccounts[0];
                  return <dl className="space-y-2" data-id="element-19313">
                            <div className="flex justify-between" data-id="element-19314">
                              <dt className="text-sm font-medium text-gray-500" data-id="element-19315">
                                Bank Name
                              </dt>
                              <dd className="text-sm text-gray-900" data-id="element-19316">
                                {primaryBank.bankName}
                              </dd>
                            </div>
                            <div className="flex justify-between" data-id="element-19317">
                              <dt className="text-sm font-medium text-gray-500" data-id="element-19318">
                                Account Name
                              </dt>
                              <dd className={`text-sm ${eftSureVerification.mismatchFields.includes('accountName') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-19319">
                                {primaryBank.accountName}
                              </dd>
                            </div>
                            <div className="flex justify-between" data-id="element-19320">
                              <dt className="text-sm font-medium text-gray-500" data-id="element-19321">
                                Account Number
                              </dt>
                              <dd className={`text-sm ${eftSureVerification.mismatchFields.includes('accountNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-19322">
                                {primaryBank.accountNumber.substring(0, 4)}
                                {'•'.repeat(Math.max(0, primaryBank.accountNumber.length - 8))}
                                {primaryBank.accountNumber.substring(primaryBank.accountNumber.length - 4)}
                              </dd>
                            </div>
                            <div className="flex justify-between" data-id="element-19323">
                              <dt className="text-sm font-medium text-gray-500" data-id="element-19324">
                                Routing Number
                              </dt>
                              <dd className={`text-sm ${eftSureVerification.mismatchFields.includes('routingNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-19325">
                                {primaryBank.routingNumber}
                              </dd>
                            </div>
                            {primaryBank.swiftCode && <div className="flex justify-between" data-id="element-19326">
                                <dt className="text-sm font-medium text-gray-500" data-id="element-19327">
                                  SWIFT/BIC Code
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-19328">
                                  {primaryBank.swiftCode}
                                </dd>
                              </div>}
                            <div className="flex items-center text-xs text-gray-500 mt-2" data-id="element-19329">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${primaryBank.complianceStatus === 'Verified' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-19330">
                                {primaryBank.complianceStatus === 'Verified' ? <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19331" /> : <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-19332" />}
                                {primaryBank.complianceStatus}
                              </span>
                              <span className="ml-2" data-id="element-19333">
                                {primaryBank.complianceProvider} •{' '}
                                {primaryBank.complianceDate}
                              </span>
                            </div>
                          </dl>;
                })() : <div className="text-sm text-gray-500 italic" data-id="element-19334">
                        No bank account information on record
                      </div>}
                  </div>
                </div>
                <div className="mt-4 flex justify-end" data-id="element-19335">
                  {eftSureVerification.status === 'match' ? <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-19336">
                      <CheckIcon className="h-4 w-4 mr-1" data-id="element-19337" />
                      Approve Bank Details
                    </button> : <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19338">
                      <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-19339" />
                      Resolve Bank Detail Issues
                    </button>}
                </div>
              </div>
            </div> : <div className="px-4 py-5 text-center" data-id="element-19340">
              <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-19341" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-19342">
                No bank details extracted
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-19343">
                No bank details were found in the current invoice.
              </p>
            </div>}
        </div>
      </div>

      {/* Bank Accounts Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19344">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19345">
          <div data-id="element-19346">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19347">
              Bank Accounts
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19348">
              Vendor payment information
            </p>
          </div>
          {isEditing && <button onClick={handleAddBankAccount} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19349">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-19350" />
              Add Bank Account
            </button>}
        </div>
        {isEditing && showBankForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-19351">
            <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-19352">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-19353">
                <h4 className="text-base font-medium text-gray-900" data-id="element-19354">
                  {editingBank ? 'Edit Bank Account' : 'Add New Bank Account'}
                </h4>
              </div>
              <div className="p-4" data-id="element-19355">
                <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-19356">
                  <div data-id="element-19357">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19358">
                      Bank Name <span className="text-red-500" data-id="element-19359">*</span>
                    </label>
                    <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newBankName ? 'border-red-300' : ''}`} value={formData.newBankName} name="newBankName" onChange={handleInputChange} placeholder="Bank name" data-id="element-19360" />
                    {errors.newBankName && <p className="mt-1 text-sm text-red-600" data-id="element-19361">
                        {errors.newBankName}
                      </p>}
                  </div>
                  <div data-id="element-19362">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19363">
                      Account Name <span className="text-red-500" data-id="element-19364">*</span>
                    </label>
                    <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newBankAccountName ? 'border-red-300' : ''}`} value={formData.newBankAccountName} name="newBankAccountName" onChange={handleInputChange} placeholder="Account holder name" data-id="element-19365" />
                    {errors.newBankAccountName && <p className="mt-1 text-sm text-red-600" data-id="element-19366">
                        {errors.newBankAccountName}
                      </p>}
                  </div>
                  <div data-id="element-19367">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19368">
                      Account Number <span className="text-red-500" data-id="element-19369">*</span>
                    </label>
                    <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newBankAccountNumber ? 'border-red-300' : ''}`} value={formData.newBankAccountNumber} name="newBankAccountNumber" onChange={handleInputChange} placeholder="Account number" data-id="element-19370" />
                    {errors.newBankAccountNumber && <p className="mt-1 text-sm text-red-600" data-id="element-19371">
                        {errors.newBankAccountNumber}
                      </p>}
                  </div>
                  <div data-id="element-19372">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19373">
                      Routing Number
                    </label>
                    <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newBankRoutingNumber} name="newBankRoutingNumber" onChange={handleInputChange} placeholder="Routing/Sort code" data-id="element-19374" />
                  </div>
                  <div data-id="element-19375">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19376">
                      SWIFT/BIC Code
                    </label>
                    <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newBankSwiftCode} name="newBankSwiftCode" onChange={handleInputChange} placeholder="SWIFT/BIC code" data-id="element-19377" />
                  </div>
                  <div data-id="element-19378">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19379">
                      Branch Name
                    </label>
                    <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newBankBranchName} name="newBankBranchName" onChange={handleInputChange} placeholder="Branch name" data-id="element-19380" />
                  </div>
                  <div data-id="element-19381">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19382">
                      Account Type
                    </label>
                    <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newBankAccountType} name="newBankAccountType" onChange={handleInputChange} data-id="element-19383">
                      {bankAccountTypes.map(type => <option key={type} value={type} data-id="element-19384">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-19385">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19386">
                      Currency
                    </label>
                    <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newBankCurrency} name="newBankCurrency" onChange={handleInputChange} data-id="element-19387">
                      {currencies.map(currency => <option key={currency} value={currency} data-id="element-19388">
                          {currency}
                        </option>)}
                    </select>
                  </div>
                  <div className="flex items-center mt-4" data-id="element-19389">
                    <input id="newBankIsPrimary" name="newBankIsPrimary" type="checkbox" checked={formData.newBankIsPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-19390" />
                    <label htmlFor="newBankIsPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-19391">
                      Primary Bank Account
                    </label>
                  </div>
                </div>
                <div className="mt-4 flex space-x-3" data-id="element-19392">
                  <button type="button" onClick={saveBankAccount} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19393">
                    {editingBank ? 'Update Bank Account' : 'Add Bank Account'}
                  </button>
                  <button type="button" onClick={() => {
                setShowBankForm(false);
                setEditingBank(null);
              }} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19394">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>}
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-19395">
          {formData.bankAccounts && formData.bankAccounts.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-19396">
              {formData.bankAccounts.map(bank => <div key={bank.id} className={`relative rounded-lg border ${bank.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-19397">
                  {bank.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-19398">
                      <CheckIcon className="h-3 w-3 mr-1" data-id="element-19399" />
                      Primary
                    </span>}
                  <div className="flex items-center space-x-3" data-id="element-19400">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-19401">
                      <div className="h-5 w-5 text-gray-500" data-id="element-19402" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19403">
                      <p className="text-sm font-medium text-gray-900 truncate" data-id="element-19404">
                        {bank.bankName}
                      </p>
                      <p className="text-xs text-gray-500 truncate" data-id="element-19405">
                        {bank.accountType} • {bank.currency}
                      </p>
                    </div>
                    <div className="flex-shrink-0" data-id="element-19406">
                      {getComplianceStatusIcon(bank.complianceStatus)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-19407">
                    <p data-id="element-19408">{bank.accountName}</p>
                    <p data-id="element-19409">
                      <span className="font-medium" data-id="element-19410">Account:</span>{' '}
                      {bank.accountNumber.substring(0, 4)}
                      {'•'.repeat(Math.max(0, bank.accountNumber.length - 8))}
                      {bank.accountNumber.substring(bank.accountNumber.length - 4)}
                    </p>
                    {bank.routingNumber && <p data-id="element-19411">
                        <span className="font-medium" data-id="element-19412">Routing:</span>{' '}
                        {bank.routingNumber}
                      </p>}
                  </div>
                  <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-19413">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bank.complianceStatus === 'Verified' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-19414">
                      {bank.complianceStatus === 'Verified' ? <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19415" /> : <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-19416" />}
                      {bank.complianceStatus}
                    </span>
                    <span className="ml-2" data-id="element-19417">
                      {bank.complianceProvider} • {bank.complianceDate}
                    </span>
                  </div>
                  {isEditing && <div className="flex justify-end space-x-2 mt-2" data-id="element-19418">
                      <button type="button" onClick={() => handleEditBankAccount(bank)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-19419">
                        Edit
                      </button>
                      <button type="button" onClick={() => removeBankAccount(bank.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19420">
                        Remove
                      </button>
                    </div>}
                </div>)}
            </div> : <div className="text-center py-4" data-id="element-19421">
              <div className="mx-auto h-12 w-12 text-gray-400" data-id="element-19422" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-19423">
                No bank accounts
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-19424">
                {isEditing ? "Add the vendor's bank account details for payments." : "This vendor doesn't have any bank accounts set up yet."}
              </p>
              {isEditing && <div className="mt-6" data-id="element-19425">
                  <button type="button" onClick={handleAddBankAccount} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19426">
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" data-id="element-19427" />
                    Add Bank Account
                  </button>
                </div>}
            </div>}
        </div>
      </div>

      {/* Addresses Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19428">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19429">
          <div data-id="element-19430">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19431">
              Addresses
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19432">
              Vendor address information
            </p>
          </div>
          {isEditing && <button onClick={handleAddAddress} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19433">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-19434" />
              Add Address
            </button>}
        </div>
        {isEditing && showAddressForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-19435">
            <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-19436">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-19437">
                <h4 className="text-base font-medium text-gray-900" data-id="element-19438">
                  {editingAddress ? 'Edit Address' : 'Add New Address'}
                </h4>
              </div>
              <div className="p-4" data-id="element-19439">
                <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-19440">
                  <div data-id="element-19441">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19442">
                      Street Address <span className="text-red-500" data-id="element-19443">*</span>
                    </label>
                    <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newAddressStreet ? 'border-red-300' : ''}`} value={formData.newAddressStreet} name="newAddressStreet" onChange={handleInputChange} placeholder="Street address" data-id="element-19444" />
                    {errors.newAddressStreet && <p className="mt-1 text-sm text-red-600" data-id="element-19445">
                        {errors.newAddressStreet}
                      </p>}
                  </div>
                  <div data-id="element-19446">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19447">
                      City <span className="text-red-500" data-id="element-19448">*</span>
                    </label>
                    <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newAddressCity ? 'border-red-300' : ''}`} value={formData.newAddressCity} name="newAddressCity" onChange={handleInputChange} placeholder="City" data-id="element-19449" />
                    {errors.newAddressCity && <p className="mt-1 text-sm text-red-600" data-id="element-19450">
                        {errors.newAddressCity}
                      </p>}
                  </div>
                  <div data-id="element-19451">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19452">
                      State/Province
                    </label>
                    <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newAddressState} name="newAddressState" onChange={handleInputChange} placeholder="State or province" data-id="element-19453" />
                  </div>
                  <div data-id="element-19454">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19455">
                      Postal Code
                    </label>
                    <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newAddressPostalCode} name="newAddressPostalCode" onChange={handleInputChange} placeholder="Postal code" data-id="element-19456" />
                  </div>
                  <div data-id="element-19457">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19458">
                      Country <span className="text-red-500" data-id="element-19459">*</span>
                    </label>
                    <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newAddressCountry ? 'border-red-300' : ''}`} value={formData.newAddressCountry} name="newAddressCountry" onChange={handleInputChange} data-id="element-19460">
                      <option value="" data-id="element-19461">Select country</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-19462">
                          {country}
                        </option>)}
                    </select>
                    {errors.newAddressCountry && <p className="mt-1 text-sm text-red-600" data-id="element-19463">
                        {errors.newAddressCountry}
                      </p>}
                  </div>
                  <div data-id="element-19464">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19465">
                      Address Type
                    </label>
                    <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newAddressType} name="newAddressType" onChange={handleInputChange} data-id="element-19466">
                      {addressTypes.map(type => <option key={type} value={type} data-id="element-19467">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div className="flex items-center mt-4" data-id="element-19468">
                    <input id="newAddressIsPrimary" name="newAddressIsPrimary" type="checkbox" checked={formData.newAddressIsPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-19469" />
                    <label htmlFor="newAddressIsPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-19470">
                      Primary Address
                    </label>
                  </div>
                </div>
                <div className="mt-4 flex space-x-3" data-id="element-19471">
                  <button type="button" onClick={saveAddress} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19472">
                    {editingAddress ? 'Update Address' : 'Add Address'}
                  </button>
                  <button type="button" onClick={() => {
                setShowAddressForm(false);
                setEditingAddress(null);
              }} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19473">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
            {errors.addresses && <p className="mt-1 text-sm text-red-600" data-id="element-19474">{errors.addresses}</p>}
          </div>}
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-19475">
          {formData.addresses && formData.addresses.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-19476">
              {formData.addresses.map(address => <div key={address.id} className={`relative rounded-lg border ${address.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-19477">
                  {address.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-19478">
                      <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-19479" />
                      Primary
                    </span>}
                  <div className="flex items-center space-x-3" data-id="element-19480">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-19481">
                      <MapPinIcon className="h-5 w-5 text-gray-500" data-id="element-19482" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19483">
                      <p className="text-sm font-medium text-gray-900 truncate" data-id="element-19484">
                        {address.addressType}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-19485">
                    <p data-id="element-19486">{address.street}</p>
                    <p data-id="element-19487">
                      {address.city}
                      {address.state && `, ${address.state}`}{' '}
                      {address.postalCode}
                    </p>
                    <p data-id="element-19488">{address.country}</p>
                  </div>
                  {isEditing && <div className="flex justify-end space-x-2 mt-2" data-id="element-19489">
                      <button type="button" onClick={() => handleEditAddress(address)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-19490">
                        Edit
                      </button>
                      <button type="button" onClick={() => removeAddress(address.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19491">
                        Remove
                      </button>
                    </div>}
                </div>)}
            </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-19492">
              No addresses found.{' '}
              {isEditing ? 'Click "Add Address" to add one.' : ''}
            </div>}
        </div>
      </div>

      {/* Contacts Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19493">
        <div className="px-4 py-5 sm:px-6" data-id="element-19494">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19495">
            Contacts
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19496">
            Vendor contact information
          </p>
        </div>
        {isEditing && <div className="px-4 py-5 border-t border-gray-200" data-id="element-19497">
            <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-19498">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-19499">
                <h4 className="text-base font-medium text-gray-900" data-id="element-19500">
                  Add New Contact
                </h4>
              </div>
              <div className="p-4" data-id="element-19501">
                <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-19502">
                  <div data-id="element-19503">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19504">
                      Contact Name <span className="text-red-500" data-id="element-19505">*</span>
                    </label>
                    <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newContactName ? 'border-red-300' : ''}`} value={formData.newContactName} name="newContactName" onChange={handleInputChange} placeholder="Full name" data-id="element-19506" />
                    {errors.newContactName && <p className="mt-1 text-sm text-red-600" data-id="element-19507">
                        {errors.newContactName}
                      </p>}
                  </div>
                  <div data-id="element-19508">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19509">
                      Email <span className="text-red-500" data-id="element-19510">*</span>
                    </label>
                    <input type="email" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.newContactEmail ? 'border-red-300' : ''}`} value={formData.newContactEmail} name="newContactEmail" onChange={handleInputChange} placeholder="Email address" data-id="element-19511" />
                    {errors.newContactEmail && <p className="mt-1 text-sm text-red-600" data-id="element-19512">
                        {errors.newContactEmail}
                      </p>}
                  </div>
                  <div data-id="element-19513">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19514">
                      Phone
                    </label>
                    <input type="tel" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newContactPhone} name="newContactPhone" onChange={handleInputChange} placeholder="Phone number" data-id="element-19515" />
                  </div>
                  <div data-id="element-19516">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19517">
                      Role
                    </label>
                    <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.newContactRole} name="newContactRole" onChange={handleInputChange} placeholder="Job title or role" data-id="element-19518" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-19519">
                  <button type="button" onClick={addContact} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19520">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-19521" />
                    Add Contact
                  </button>
                </div>
              </div>
            </div>
            {errors.contacts && <p className="mt-1 text-sm text-red-600" data-id="element-19522">{errors.contacts}</p>}
          </div>}
        <div className="border-t border-gray-200" data-id="element-19523">
          {formData.contacts && formData.contacts.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-19524">
              {formData.contacts.map(contact => <li key={contact.id} className="px-4 py-4" data-id="element-19525">
                  <div className="flex items-center justify-between" data-id="element-19526">
                    <div data-id="element-19527">
                      <h4 className="text-base font-medium text-gray-900" data-id="element-19528">
                        {contact.name}
                      </h4>
                      <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-19529">
                        <MailIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-19530" />
                        <a href={`mailto:${contact.email}`} className="text-indigo-600 hover:text-indigo-900" data-id="element-19531">
                          {contact.email}
                        </a>
                      </div>
                      {contact.phone && <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-19532">
                          <PhoneIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-19533" />
                          {contact.phone}
                        </div>}
                      {contact.role && <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-19534">
                          <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-19535" />
                          {contact.role}
                        </div>}
                    </div>
                    {isEditing && <button type="button" onClick={() => removeContact(contact.id)} className="text-red-600 hover:text-red-900" data-id="element-19536">
                        <TrashIcon className="h-4 w-4" data-id="element-19537" />
                      </button>}
                  </div>
                </li>)}
            </ul> : <div className="px-4 py-5 text-center text-sm text-gray-500" data-id="element-19538">
              No contacts added yet.
            </div>}
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19539">
        <div className="px-4 py-5 sm:px-6" data-id="element-19540">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19541">
            Documents
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19542">
            Vendor documentation and certifications
          </p>
        </div>
        {isEditing && <div className="px-4 py-5 border-t border-gray-200" data-id="element-19543">
            <FileUpload onFileUpload={handleFileUpload} data-id="element-19544" />
            {files.length > 0 && <div className="mt-4" data-id="element-19545">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-19546">
                  New Files to Upload
                </h4>
                <ul className="border rounded-md divide-y divide-gray-200" data-id="element-19547">
                  {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-19548">
                      <div className="flex items-center" data-id="element-19549">
                        <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19550" />
                        <span className="ml-2 flex-1 w-0 truncate" data-id="element-19551">
                          {file.name}
                        </span>
                      </div>
                      <div className="flex items-center" data-id="element-19552">
                        <span className="text-xs text-gray-500 mr-4" data-id="element-19553">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                        <button type="button" onClick={() => removeFile(index)} className="text-red-600 hover:text-red-900" data-id="element-19554">
                          <TrashIcon className="h-4 w-4" data-id="element-19555" />
                        </button>
                      </div>
                    </li>)}
                </ul>
              </div>}
          </div>}
        <div className="border-t border-gray-200" data-id="element-19556">
          {vendor.documents && vendor.documents.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-19557">
              {vendor.documents.map(doc => <li key={doc.id} className="px-4 py-4" data-id="element-19558">
                  <div className="flex items-center justify-between" data-id="element-19559">
                    <div className="flex items-center" data-id="element-19560">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-19561" />
                      <div data-id="element-19562">
                        <h4 className="text-base font-medium text-gray-900" data-id="element-19563">
                          {doc.name}
                        </h4>
                        <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500" data-id="element-19564">
                          <span className="mr-3" data-id="element-19565">Type: {doc.type}</span>
                          <span className="mr-3" data-id="element-19566">
                            Uploaded: {doc.uploadDate}
                          </span>
                          {doc.expiryDate && <span className="mr-3" data-id="element-19567">
                              <CalendarIcon className="inline-block mr-1 h-4 w-4 text-gray-400" data-id="element-19568" />
                              Expires: {doc.expiryDate}
                            </span>}
                        </div>
                      </div>
                    </div>
                    <div data-id="element-19569">{getDocumentStatusBadge(doc.status)}</div>
                  </div>
                </li>)}
            </ul> : <div className="px-4 py-5 text-center text-sm text-gray-500" data-id="element-19570">
              No documents uploaded yet.
            </div>}
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19571">
        <div className="px-4 py-5 sm:px-6" data-id="element-19572">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19573">
            Activity Timeline
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19574">
            Recent activity and updates
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-19575">
          <ul className="space-y-4" data-id="element-19576">
            <li className="relative pb-4" data-id="element-19577">
              <div className="absolute top-0 left-0 h-full w-0.5 bg-gray-200" data-id="element-19578"></div>
              <div className="relative flex items-start group" data-id="element-19579">
                <span className="h-4 w-4 rounded-full bg-indigo-500 flex items-center justify-center ring-4 ring-white" data-id="element-19580">
                  <span className="h-2 w-2 rounded-full bg-white" data-id="element-19581"></span>
                </span>
                <div className="ml-4 min-w-0 flex-1" data-id="element-19582">
                  <div className="text-sm font-medium text-gray-900" data-id="element-19583">
                    Vendor record updated
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-19584">
                    <span data-id="element-19585">{vendor.lastUpdated}</span>
                  </div>
                </div>
              </div>
            </li>
            <li className="relative pb-4" data-id="element-19586">
              <div className="absolute top-0 left-0 h-full w-0.5 bg-gray-200" data-id="element-19587"></div>
              <div className="relative flex items-start group" data-id="element-19588">
                <span className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center ring-4 ring-white" data-id="element-19589">
                  <span className="h-2 w-2 rounded-full bg-white" data-id="element-19590"></span>
                </span>
                <div className="ml-4 min-w-0 flex-1" data-id="element-19591">
                  <div className="text-sm font-medium text-gray-900" data-id="element-19592">
                    Vendor onboarded
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-19593">
                    <span data-id="element-19594">{vendor.onboardingDate}</span>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>;
}